/**
 * openScanModal — Vanilla JS fullscreen modal for document scanning.
 * Uses ScanEngine (singleton) for edge detection.
 * Creates/destroys DOM on each open/close. CSS injected once.
 *
 * Usage:
 *   openScanModal({ docType: 'de_minimis', onComplete: (file) => { ... } });
 */
import { jsPDF } from 'jspdf';
import { loadOpenCV, isOpenCVReady, detectEdges } from './ScanEngine';

// ─── CSS (injected once) ────────────────────────────────────────
let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const s = document.createElement('style');
  s.id = 'scan-modal-css';
  s.textContent = `
    .scm{position:fixed;inset:0;z-index:99999;background:#0a0a0f;display:flex;flex-direction:column;font-family:-apple-system,BlinkMacSystemFont,'SF Pro','Segoe UI',system-ui,sans-serif;color:#e2e8f0;-webkit-user-select:none;user-select:none;padding:6px 10px;padding-bottom:max(6px,env(safe-area-inset-bottom,0px))}
    .scm *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
    .scm-hdr{display:flex;align-items:center;justify-content:space-between;flex-shrink:0;padding:4px 0}
    .scm-title{font-size:15px;font-weight:700;letter-spacing:-0.3px}
    .scm-close{width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,.12);border:none;color:#e2e8f0;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px}
    .scm-close:active{background:rgba(255,255,255,.22)}
    .scm-badge{font-size:10px;padding:3px 10px;border-radius:16px;background:#1e1e2e;display:inline-flex;align-items:center;gap:4px}
    .scm-bdot{width:5px;height:5px;border-radius:50%;background:#f59e0b}
    .scm-badge.ok .scm-bdot{background:#10b981}
    .scm-badge.ok{color:#10b981}
    .scm-steps{display:flex;gap:5px;justify-content:center;padding:3px 0 5px;flex-shrink:0}
    .scm-dot{width:7px;height:7px;border-radius:50%;background:#1e1e2e;transition:.3s}
    .scm-dot.on{background:#6366f1;width:20px;border-radius:3px}
    .scm-dot.done{background:#10b981}
    .scm-scr{display:none;flex:1;flex-direction:column;min-height:0;overflow:hidden}
    .scm-scr.on{display:flex}
    .scm-center{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px}
    .scm-icon{width:68px;height:68px;border-radius:20px;background:linear-gradient(135deg,#6366f1,#7c3aed);display:flex;align-items:center;justify-content:center}
    .scm-icon svg{width:34px;height:34px;stroke:#fff;fill:none;stroke-width:1.5}
    .scm-info{text-align:center}
    .scm-info h2{font-size:15px;font-weight:600;margin-bottom:2px}
    .scm-info p{font-size:11px;color:#94a3b8;line-height:1.4;max-width:240px}
    .scm-btn{width:100%;padding:12px;border:none;border-radius:12px;font-size:14px;font-weight:600;cursor:pointer;transition:.15s}
    .scm-btn:active{transform:scale(.98)}
    .scm-bp{background:#6366f1;color:#fff}.scm-bp:active{background:#818cf8}
    .scm-bs{background:#1e1e2e;color:#e2e8f0;border:1px solid #2a2a3e}.scm-bs:active{background:#2a2a3e}
    .scm-bg{background:#10b981;color:#fff}.scm-bg:active{background:#059669}
    .scm-row{display:flex;gap:8px;flex-shrink:0;padding-top:6px}
    .scm-row .scm-btn{flex:1}
    .scm-pginfo{display:flex;align-items:center;gap:6px;flex-shrink:0;padding:2px 0}
    .scm-pgcount{font-size:11px;color:#94a3b8;flex-shrink:0}
    .scm-thumbs{display:flex;gap:4px;overflow-x:auto;flex:1;padding:2px 0}
    .scm-thumb{width:32px;height:44px;border-radius:4px;border:2px solid #2a2a3e;object-fit:cover;flex-shrink:0;cursor:pointer;background:#14141f}
    .scm-thumb.on{border-color:#6366f1}
    .scm-st{text-align:center;font-size:10px;flex-shrink:0;display:flex;align-items:center;justify-content:center;gap:4px;padding:2px 0}
    .scm-si{width:5px;height:5px;border-radius:50%;flex-shrink:0}
    .scm-wrap{flex:1;position:relative;display:flex;align-items:center;justify-content:center;overflow:hidden;border-radius:12px;background:#000;touch-action:none;min-height:0}
    .scm-wrap img{display:block;max-width:100%;max-height:100%;object-fit:contain}
    .scm-svg{position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:2}
    .scm-h{position:absolute;z-index:10;pointer-events:auto;width:44px;height:44px;transform:translate(-50%,-50%);touch-action:none;cursor:grab}
    .scm-hi{width:100%;height:100%;border-radius:50%;background:#6366f1;border:3px solid #fff;box-shadow:0 2px 10px rgba(99,102,241,.5);display:flex;align-items:center;justify-content:center;transition:.1s}
    .scm-hi::after{content:'';width:7px;height:7px;border-radius:50%;background:#fff}
    .scm-h.drag .scm-hi{background:#818cf8;box-shadow:0 0 20px rgba(99,102,241,.8);transform:scale(1.15)}
    .scm-res{flex:1;display:flex;align-items:center;justify-content:center;border-radius:12px;background:#14141f;overflow:hidden;min-height:0;margin-top:4px}
    .scm-res canvas{max-width:100%;max-height:100%;display:block}
    .scm-ft{display:flex;background:#1e1e2e;border-radius:8px;overflow:hidden;border:1px solid #2a2a3e;flex-shrink:0}
    .scm-fb{flex:1;padding:8px;text-align:center;font-size:11px;font-weight:600;color:#94a3b8;cursor:pointer;border:none;background:transparent;transition:.2s}
    .scm-fb.on{background:#6366f1;color:#fff;border-radius:6px}
    .scm-ld{position:absolute;inset:0;background:rgba(10,10,15,.92);display:flex;align-items:center;justify-content:center;flex-direction:column;gap:12px;z-index:50}
    .scm-ld.off{display:none}
    .scm-sp{width:28px;height:28px;border:3px solid #1e1e2e;border-top-color:#6366f1;border-radius:50%;animation:scm-spin .7s linear infinite}
    @keyframes scm-spin{to{transform:rotate(360deg)}}
    .scm-lt{font-size:12px;color:#94a3b8}
  `;
  document.head.appendChild(s);
}

// ─── MODAL HTML ─────────────────────────────────────────────────
const MODAL_HTML = `
  <div class="scm-hdr">
    <span class="scm-title">Scan Document</span>
    <div style="display:flex;align-items:center;gap:8px">
      <span class="scm-badge" data-s="badge"><span class="scm-bdot"></span><span data-s="badge-txt">Chargement...</span></span>
      <button class="scm-close" data-s="close">&times;</button>
    </div>
  </div>
  <div class="scm-steps"><div class="scm-dot on" data-s="d0"></div><div class="scm-dot" data-s="d1"></div><div class="scm-dot" data-s="d2"></div></div>
  <div class="scm-scr on" data-s="s0">
    <div class="scm-center">
      <div class="scm-icon"><svg viewBox="0 0 48 48"><rect x="6" y="6" width="36" height="36" rx="4"/><path d="M16 22l4 4 8-8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
      <div class="scm-info"><h2>Scanner un document</h2><p>Prenez en photo le document pour le numeriser</p></div>
    </div>
    <button class="scm-btn scm-bp" data-s="capture">Ouvrir la camera</button>
    <input type="file" accept="image/*" capture="environment" data-s="file-input" style="display:none">
  </div>
  <div class="scm-scr" data-s="s1">
    <div class="scm-st" data-s="crop-status"><span class="scm-si" style="background:#f59e0b"></span><span data-s="crop-txt">Placez les coins sur le document</span></div>
    <div class="scm-wrap" data-s="crop-wrap">
      <img data-s="crop-img" src="" alt="">
      <svg class="scm-svg" data-s="crop-svg"></svg>
      <div class="scm-h" data-s="h0"><div class="scm-hi"></div></div>
      <div class="scm-h" data-s="h1"><div class="scm-hi"></div></div>
      <div class="scm-h" data-s="h2"><div class="scm-hi"></div></div>
      <div class="scm-h" data-s="h3"><div class="scm-hi"></div></div>
    </div>
    <div class="scm-row">
      <button class="scm-btn scm-bs" data-s="retake">Reprendre</button>
      <button class="scm-btn scm-bp" data-s="validate">Valider</button>
    </div>
  </div>
  <div class="scm-scr" data-s="s2">
    <div class="scm-pginfo">
      <span class="scm-pgcount" data-s="pgcount">Page 1</span>
      <div class="scm-thumbs" data-s="thumbs"></div>
    </div>
    <div class="scm-ft">
      <button class="scm-fb on" data-s="f-scan">Scan N&B</button>
      <button class="scm-fb" data-s="f-orig">Original</button>
      <button class="scm-fb" data-s="f-gray">Niveaux de gris</button>
    </div>
    <div class="scm-res"><canvas data-s="result"></canvas></div>
    <div class="scm-row">
      <button class="scm-btn scm-bs" data-s="recrop">Recadrer</button>
      <button class="scm-btn scm-bp" data-s="addpage">+ Page</button>
      <button class="scm-btn scm-bg" data-s="save">Terminer</button>
    </div>
  </div>
  <div class="scm-ld off" data-s="loader"><div class="scm-sp"></div><div class="scm-lt" data-s="loader-txt">Traitement...</div></div>
`;

// ─── MAIN ───────────────────────────────────────────────────────
export function openScanModal({ docType, onComplete, onCancel }) {
  ensureStyles();

  // Create overlay
  const el = document.createElement('div');
  el.className = 'scm';
  el.setAttribute('data-testid', 'scan-modal');
  el.innerHTML = MODAL_HTML;
  document.body.appendChild(el);

  // Quick selector
  const $ = (sel) => el.querySelector(`[data-s="${sel}"]`);

  // ── State ──
  let originalImage = null;
  let imgDataURL = '';
  let corners = [];
  let warpedData = null;
  let filter = 'scan';
  let imgRect = { x: 0, y: 0, w: 0, h: 0 };
  const pages = []; // each entry: { dataURL } (JPEG data URL of the filtered page)
  const listeners = [];

  function on(target, evt, fn, opts) {
    target.addEventListener(evt, fn, opts);
    listeners.push([target, evt, fn, opts]);
  }

  // ── Loading ──
  function showLoad(t) { $('loader-txt').textContent = t; $('loader').classList.remove('off'); }
  function hideLoad() { $('loader').classList.add('off'); }

  // ── Navigation ──
  function goStep(n) {
    ['s0', 's1', 's2'].forEach((k, i) => {
      $(k).classList.toggle('on', i === n);
      const dot = $('d' + i);
      dot.classList.toggle('on', i === n);
      dot.classList.toggle('done', i < n);
    });
    if (n === 1) requestAnimationFrame(() => requestAnimationFrame(() => { computeRect(); posHandles(); drawOverlay(); }));
  }

  // ── Cleanup & close ──
  function destroy() {
    listeners.forEach(([t, e, f, o]) => t.removeEventListener(e, f, o));
    listeners.length = 0;
    originalImage = null;
    imgDataURL = '';
    warpedData = null;
    corners = [];
    el.remove();
  }

  function close() { destroy(); if (onCancel) onCancel(); }
  function finish(file) { destroy(); if (onComplete) onComplete(file); }

  // ── OpenCV status ──
  loadOpenCV()
    .then(() => { $('badge').classList.add('ok'); $('badge-txt').textContent = 'Detection prete'; })
    .catch(() => { $('badge-txt').textContent = 'Detection indisponible'; });

  // ── Capture ──
  on($('capture'), 'click', () => $('file-input').click());
  on($('file-input'), 'change', (e) => {
    const file = e.target.files[0];
    if (!file) return;
    showLoad('Chargement...');
    const reader = new FileReader();
    reader.onload = (ev) => {
      imgDataURL = ev.target.result;
      const img = new Image();
      img.onload = () => { originalImage = img; initCrop(); };
      img.src = imgDataURL;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  });

  // ── Init crop ──
  function initCrop() {
    $('crop-img').src = imgDataURL;
    showLoad('Detection du document...');
    setTimeout(() => {
      const detected = detectEdges(originalImage);
      const iw = originalImage.naturalWidth || originalImage.width;
      const ih = originalImage.naturalHeight || originalImage.height;
      if (detected) {
        corners = detected;
        $('crop-status').querySelector('.scm-si').style.background = '#10b981';
        $('crop-txt').textContent = 'Document detecte — ajustez si necessaire';
      } else {
        const m = 0.1;
        corners = [{ x: iw * m, y: ih * m }, { x: iw * (1 - m), y: ih * m }, { x: iw * (1 - m), y: ih * (1 - m) }, { x: iw * m, y: ih * (1 - m) }];
        $('crop-status').querySelector('.scm-si').style.background = '#f59e0b';
        $('crop-txt').textContent = isOpenCVReady() ? 'Non detecte — placez les coins manuellement' : 'Placez les coins sur le document';
      }
      hideLoad();
      goStep(1);
      setTimeout(() => { computeRect(); posHandles(); drawOverlay(); }, 100);
    }, 60);
  }

  // ── Coordinate mapping ──
  function computeRect() {
    const wrap = $('crop-wrap');
    const img = $('crop-img');
    const wr = wrap.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    imgRect = { x: ir.left - wr.left, y: ir.top - wr.top, w: ir.width, h: ir.height };
  }

  function toWrapper(pt) {
    const iw = originalImage.naturalWidth || originalImage.width;
    const ih = originalImage.naturalHeight || originalImage.height;
    return { x: imgRect.x + (pt.x / iw) * imgRect.w, y: imgRect.y + (pt.y / ih) * imgRect.h };
  }

  function toImage(wx, wy) {
    const iw = originalImage.naturalWidth || originalImage.width;
    const ih = originalImage.naturalHeight || originalImage.height;
    return { x: ((wx - imgRect.x) / imgRect.w) * iw, y: ((wy - imgRect.y) / imgRect.h) * ih };
  }

  function posHandles() {
    computeRect();
    for (let i = 0; i < 4; i++) {
      const h = $('h' + i);
      const p = toWrapper(corners[i]);
      h.style.left = p.x + 'px';
      h.style.top = p.y + 'px';
    }
  }

  function drawOverlay() {
    const svg = $('crop-svg');
    const pts = corners.map(c => toWrapper(c));
    const w = svg.clientWidth || 300, h = svg.clientHeight || 400;
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    const poly = pts.map(p => p.x + ',' + p.y).join(' ');
    svg.innerHTML =
      `<defs><mask id="scm-m"><rect width="${w}" height="${h}" fill="white"/><polygon points="${poly}" fill="black"/></mask></defs>` +
      `<rect width="${w}" height="${h}" fill="rgba(0,0,0,.5)" mask="url(#scm-m)"/>` +
      `<polygon points="${poly}" fill="none" stroke="#6366f1" stroke-width="2" stroke-linejoin="round"/>` +
      `<polygon points="${poly}" fill="rgba(99,102,241,.06)"/>`;
  }

  // ── Drag handles ──
  let dragIdx = -1;
  function touchPos(e) {
    const wr = $('crop-wrap').getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - wr.left, y: t.clientY - wr.top };
  }

  for (let i = 0; i < 4; i++) {
    const h = $('h' + i);
    const start = (e) => { e.preventDefault(); e.stopPropagation(); dragIdx = i; h.classList.add('drag'); };
    on(h, 'touchstart', start, { passive: false });
    on(h, 'mousedown', start);
  }
  const move = (e) => {
    if (dragIdx < 0) return;
    e.preventDefault();
    const p = touchPos(e);
    const ip = toImage(p.x, p.y);
    const iw = originalImage.naturalWidth || originalImage.width;
    const ih = originalImage.naturalHeight || originalImage.height;
    ip.x = Math.max(0, Math.min(iw, ip.x));
    ip.y = Math.max(0, Math.min(ih, ip.y));
    corners[dragIdx] = ip;
    posHandles();
    drawOverlay();
  };
  const end = () => { if (dragIdx >= 0) $('h' + dragIdx).classList.remove('drag'); dragIdx = -1; };
  on(document, 'touchmove', move, { passive: false });
  on(document, 'mousemove', move);
  on(document, 'touchend', end);
  on(document, 'mouseup', end);
  on(window, 'resize', () => { if (originalImage) { posHandles(); drawOverlay(); } });

  // ── Multi-page management ──
  function storePage() {
    const c = $('result');
    pages.push({ dataURL: c.toDataURL('image/jpeg', 0.88), w: c.width, h: c.height });
    updatePageUI();
  }

  // Update the last stored page with current canvas (after filter change)
  function updateCurrentPage() {
    if (pages.length === 0) return;
    const c = $('result');
    pages[pages.length - 1] = { dataURL: c.toDataURL('image/jpeg', 0.88), w: c.width, h: c.height };
    updatePageUI();
  }

  function updatePageUI() {
    $('pgcount').textContent = 'Page ' + pages.length;
    $('save').textContent = pages.length > 1 ? 'Terminer (' + pages.length + ' pages)' : 'Terminer';
    // Render thumbnails
    const box = $('thumbs');
    box.innerHTML = '';
    pages.forEach((pg, i) => {
      const img = document.createElement('img');
      img.className = 'scm-thumb' + (i === pages.length - 1 ? ' on' : '');
      img.src = pg.dataURL;
      img.title = 'Page ' + (i + 1);
      box.appendChild(img);
    });
    // Scroll to end
    box.scrollLeft = box.scrollWidth;
  }

  // ── Buttons: retake, validate, recrop, addpage, save, close ──
  on($('retake'), 'click', () => goStep(0));
  on($('recrop'), 'click', () => {
    // Remove last page (will be re-added after re-validate)
    if (pages.length > 0) pages.pop();
    updatePageUI();
    goStep(1);
  });
  on($('addpage'), 'click', () => {
    // Reset for next page capture, keep pages array and current filter preference
    originalImage = null;
    imgDataURL = '';
    corners = [];
    warpedData = null;
    // Keep filter preference for next page (don't reset to 'scan')
    goStep(0);
  });
  on($('close'), 'click', close);

  // ── Perspective warp (A4 output) ──
  on($('validate'), 'click', () => {
    showLoad('Redressement...');
    setTimeout(() => {
      try {
        const src = corners;
        const wT = Math.hypot(src[1].x - src[0].x, src[1].y - src[0].y);
        const wB = Math.hypot(src[2].x - src[3].x, src[2].y - src[3].y);
        const hL = Math.hypot(src[3].x - src[0].x, src[3].y - src[0].y);
        const hR = Math.hypot(src[2].x - src[1].x, src[2].y - src[1].y);
        const baseW = Math.max(wT, wB), baseH = Math.max(hL, hR);
        const A4 = 297 / 210;
        let outW, outH;
        if (baseH >= baseW) { outW = Math.round(baseW); outH = Math.round(outW * A4); }
        else { outH = Math.round(baseH); outW = Math.round(outH * A4); }
        const sc = Math.min(1, 2000 / Math.max(outW, outH));
        outW = Math.round(outW * sc); outH = Math.round(outH * sc);

        const srcC = document.createElement('canvas');
        srcC.width = originalImage.naturalWidth || originalImage.width;
        srcC.height = originalImage.naturalHeight || originalImage.height;
        srcC.getContext('2d').drawImage(originalImage, 0, 0);
        const srcData = srcC.getContext('2d').getImageData(0, 0, srcC.width, srcC.height);

        const M = solveH(
          [0, 0, outW, 0, outW, outH, 0, outH],
          [src[0].x, src[0].y, src[1].x, src[1].y, src[2].x, src[2].y, src[3].x, src[3].y]
        );

        const outC = document.createElement('canvas');
        outC.width = outW; outC.height = outH;
        const outCtx = outC.getContext('2d');
        const outImg = outCtx.createImageData(outW, outH);
        const sd = srcData.data, od = outImg.data, sw = srcC.width, sh = srcC.height;

        for (let y = 0; y < outH; y++) {
          for (let x = 0; x < outW; x++) {
            const d = M[6] * x + M[7] * y + M[8];
            const sx = (M[0] * x + M[1] * y + M[2]) / d;
            const sy = (M[3] * x + M[4] * y + M[5]) / d;
            if (sx >= 0 && sx < sw - 1 && sy >= 0 && sy < sh - 1) {
              const ix = Math.floor(sx), iy = Math.floor(sy), fx = sx - ix, fy = sy - iy;
              const i00 = (iy * sw + ix) * 4, i10 = i00 + 4, i01 = i00 + sw * 4, i11 = i01 + 4;
              const oi = (y * outW + x) * 4;
              for (let c = 0; c < 3; c++) od[oi + c] = Math.round(sd[i00 + c] * (1 - fx) * (1 - fy) + sd[i10 + c] * fx * (1 - fy) + sd[i01 + c] * (1 - fx) * fy + sd[i11 + c] * fx * fy);
              od[oi + 3] = 255;
            }
          }
        }
        outCtx.putImageData(outImg, 0, 0);
        warpedData = outCtx.getImageData(0, 0, outW, outH);
        // Keep current filter selection (don't force to 'scan')
        renderResult();
        // Store page after rendering with current filter
        storePage();
        goStep(2);
      } catch (e) { console.error(e); alert('Erreur: ' + e.message); }
      hideLoad();
    }, 50);
  });

  // ── Filters ──
  // ── Image processing helpers ──
  function contrastStretch(arr, len, lo, hi) {
    // Find actual min/max using percentiles to ignore outliers
    const hist = new Uint32Array(256);
    for (let i = 0; i < len; i++) hist[arr[i]]++;
    const total = len;
    const pLo = lo, pHi = hi;
    let cumLo = 0, cumHi = 0, vMin = 0, vMax = 255;
    for (let i = 0; i < 256; i++) {
      cumLo += hist[i];
      if (cumLo >= total * pLo) { vMin = i; break; }
    }
    for (let i = 255; i >= 0; i--) {
      cumHi += hist[i];
      if (cumHi >= total * (1 - pHi)) { vMax = i; break; }
    }
    if (vMax <= vMin) { vMax = vMin + 1; }
    const scale = 255 / (vMax - vMin);
    const lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) lut[i] = Math.max(0, Math.min(255, Math.round((i - vMin) * scale)));
    return lut;
  }

  function unsharpMask(d, w, h, channels) {
    // Simple 3x3 unsharp mask for sharpening
    const src = new Uint8ClampedArray(d);
    const strength = 0.4;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const idx = (y * w + x) * 4;
        for (let c = 0; c < channels; c++) {
          // 3x3 blur approximation
          const blur = (
            src[((y-1)*w+(x-1))*4+c] + src[((y-1)*w+x)*4+c]*2 + src[((y-1)*w+(x+1))*4+c] +
            src[(y*w+(x-1))*4+c]*2 + src[(y*w+x)*4+c]*4 + src[(y*w+(x+1))*4+c]*2 +
            src[((y+1)*w+(x-1))*4+c] + src[((y+1)*w+x)*4+c]*2 + src[((y+1)*w+(x+1))*4+c]
          ) / 16;
          const sharp = src[idx+c] + (src[idx+c] - blur) * strength;
          d[idx+c] = Math.max(0, Math.min(255, Math.round(sharp)));
        }
      }
    }
  }

  function renderResult() {
    if (!warpedData) return;
    const c = $('result');
    const w = warpedData.width, h = warpedData.height;
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    const fd = new ImageData(new Uint8ClampedArray(warpedData.data), w, h);
    const d = fd.data;
    const n = w * h;

    if (filter === 'original') {
      // ── Enhanced Color: auto white-balance + contrast + sharpen ──
      // 1. Estimate white point from brightest 5% of pixels per channel
      const rHist = new Uint32Array(256), gHist = new Uint32Array(256), bHist = new Uint32Array(256);
      for (let i = 0; i < d.length; i += 4) { rHist[d[i]]++; gHist[d[i+1]]++; bHist[d[i+2]]++; }
      const findPct = (hist, pct) => { let cum = 0; for (let i = 255; i >= 0; i--) { cum += hist[i]; if (cum >= n * pct) return Math.max(i, 1); } return 255; };
      const rMax = findPct(rHist, 0.05), gMax = findPct(gHist, 0.05), bMax = findPct(bHist, 0.05);
      const rScale = 255 / rMax, gScale = 255 / gMax, bScale = 255 / bMax;

      // 2. Apply white balance + contrast stretch
      const gray = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        const idx = i * 4;
        d[idx]   = Math.min(255, Math.round(d[idx]   * rScale));
        d[idx+1] = Math.min(255, Math.round(d[idx+1] * gScale));
        d[idx+2] = Math.min(255, Math.round(d[idx+2] * bScale));
        gray[i] = Math.round(d[idx] * 0.299 + d[idx+1] * 0.587 + d[idx+2] * 0.114);
      }
      const lut = contrastStretch(gray, n, 0.005, 0.995);

      // 3. Apply contrast LUT to all channels proportionally
      for (let i = 0; i < n; i++) {
        const idx = i * 4;
        const g = gray[i];
        if (g === 0) continue;
        const factor = lut[g] / g;
        d[idx]   = Math.min(255, Math.round(d[idx]   * factor));
        d[idx+1] = Math.min(255, Math.round(d[idx+1] * factor));
        d[idx+2] = Math.min(255, Math.round(d[idx+2] * factor));
      }

      // 4. Sharpen
      unsharpMask(d, w, h, 3);
      ctx.putImageData(fd, 0, 0);
      return;
    }

    if (filter === 'gray') {
      // ── Enhanced Grayscale: convert + stretch contrast + whiten bg + sharpen ──
      // 1. Convert to grayscale
      const gray = new Uint8Array(n);
      for (let i = 0; i < n; i++) {
        const idx = i * 4;
        gray[i] = Math.round(d[idx] * 0.299 + d[idx+1] * 0.587 + d[idx+2] * 0.114);
      }

      // 2. Contrast stretch (clip 0.5% darkest, 0.5% brightest)
      const lut = contrastStretch(gray, n, 0.005, 0.995);

      // 3. Apply LUT + background whitening (push light grays toward white)
      for (let i = 0; i < n; i++) {
        let v = lut[gray[i]];
        // Background whitening: values above 180 get pushed toward 255
        if (v > 180) v = Math.min(255, Math.round(180 + (v - 180) * (255 - 180) / (255 - 180) * 1.4));
        if (v > 230) v = 255; // clip near-white to pure white
        const idx = i * 4;
        d[idx] = d[idx+1] = d[idx+2] = v;
      }

      // 4. Sharpen
      unsharpMask(d, w, h, 1);
      ctx.putImageData(fd, 0, 0);
      return;
    }

    // ── Enhanced B&W (Scan): pre-normalize + adaptive threshold + denoise ──
    // 1. Convert to grayscale + contrast stretch
    const ga = new Float32Array(n);
    const gaU8 = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      gaU8[i] = Math.round(d[i*4] * 0.299 + d[i*4+1] * 0.587 + d[i*4+2] * 0.114);
    }
    const lut = contrastStretch(gaU8, n, 0.01, 0.99);
    for (let i = 0; i < n; i++) ga[i] = lut[gaU8[i]];

    // 2. Adaptive threshold with integral image
    const intg = new Float64Array((w + 1) * (h + 1));
    for (let y = 0; y < h; y++) {
      let rs = 0;
      for (let x = 0; x < w; x++) {
        rs += ga[y * w + x];
        intg[(y + 1) * (w + 1) + (x + 1)] = intg[y * (w + 1) + (x + 1)] + rs;
      }
    }
    const bs = Math.max(15, Math.round(Math.min(w, h) / 16));
    const C = 8;
    const out = new Uint8Array(n);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const x0 = Math.max(0, x - bs), y0 = Math.max(0, y - bs);
        const x1 = Math.min(w, x + bs + 1), y1 = Math.min(h, y + bs + 1);
        const area = (x1 - x0) * (y1 - y0);
        const sum = intg[y1*(w+1)+x1] - intg[y0*(w+1)+x1] - intg[y1*(w+1)+x0] + intg[y0*(w+1)+x0];
        out[y * w + x] = ga[y * w + x] > (sum / area - C) ? 255 : 0;
      }
    }

    // 3. Simple denoise: remove isolated black pixels (salt noise)
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        if (out[y*w+x] === 0) {
          let whiteNeighbors = 0;
          for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
              if (dy === 0 && dx === 0) continue;
              if (out[(y+dy)*w+(x+dx)] === 255) whiteNeighbors++;
            }
          }
          if (whiteNeighbors >= 7) out[y*w+x] = 255; // isolated pixel → remove
        }
      }
    }

    for (let i = 0; i < n; i++) {
      const idx = i * 4;
      d[idx] = d[idx+1] = d[idx+2] = out[i];
    }
    ctx.putImageData(fd, 0, 0);
  }

  ['scan', 'orig', 'gray'].forEach(f => {
    on($('f-' + f), 'click', () => {
      const name = f === 'orig' ? 'original' : f === 'gray' ? 'gray' : 'scan';
      filter = name;
      el.querySelectorAll('.scm-fb').forEach(b => b.classList.remove('on'));
      $('f-' + f).classList.add('on');
      renderResult();
      updateCurrentPage(); // Sync stored page with new filter
    });
  });

  // ── Save (generate multi-page PDF → File → callback) ──
  on($('save'), 'click', () => {
    if (pages.length === 0) return;
    showLoad('Generation du PDF...');
    setTimeout(() => {
      try {
        // Use first page to determine orientation
        const first = pages[0];
        const orient = first.w > first.h ? 'landscape' : 'portrait';
        const pdf = new jsPDF({ orientation: orient, unit: 'mm', format: 'a4' });

        pages.forEach((pg, i) => {
          if (i > 0) {
            const pgOrient = pg.w > pg.h ? 'landscape' : 'portrait';
            pdf.addPage('a4', pgOrient);
          }
          const pw = pdf.internal.pageSize.getWidth();
          const ph = pdf.internal.pageSize.getHeight();
          const mg = 5;
          const s = Math.min((pw - mg * 2) / pg.w, (ph - mg * 2) / pg.h);
          const iw = pg.w * s, ih = pg.h * s;
          pdf.addImage(pg.dataURL, 'JPEG', (pw - iw) / 2, (ph - ih) / 2, iw, ih);
        });

        const blob = pdf.output('blob');
        const file = new File([blob], `scan_${docType || 'document'}.pdf`, { type: 'application/pdf' });
        finish(file);
      } catch (e) { console.error(e); alert('Erreur: ' + e.message); }
      hideLoad();
    }, 60);
  });
}

// ─── HOMOGRAPHY SOLVER ──────────────────────────────────────────
function solveH(dst, src) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const dx = dst[i * 2], dy = dst[i * 2 + 1], sx = src[i * 2], sy = src[i * 2 + 1];
    A.push([dx, dy, 1, 0, 0, 0, -sx * dx, -sx * dy]);
    A.push([0, 0, 0, dx, dy, 1, -sy * dx, -sy * dy]);
    b.push(sx); b.push(sy);
  }
  const n = 8;
  for (let col = 0; col < n; col++) {
    let mr = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(A[r][col]) > Math.abs(A[mr][col])) mr = r;
    [A[col], A[mr]] = [A[mr], A[col]]; [b[col], b[mr]] = [b[mr], b[col]];
    const piv = A[col][col];
    if (Math.abs(piv) < 1e-10) continue;
    for (let r = col + 1; r < n; r++) {
      const f = A[r][col] / piv;
      for (let j = col; j < n; j++) A[r][j] -= f * A[col][j];
      b[r] -= f * b[col];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let s = b[i];
    for (let j = i + 1; j < n; j++) s -= A[i][j] * x[j];
    x[i] = s / A[i][i];
  }
  x.push(1);
  return x;
}
